import React, { useState } from 'react';
import Button from '@material-ui/core/Button/';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Grow from '@material-ui/core/Grow';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import Paper from '@material-ui/core/Paper';
import Popper from '@material-ui/core/Popper';
import type { Theme } from '@material-ui/core/styles/createMuiTheme';
import withStyles, { type WithStyles } from '@material-ui/core/styles/withStyles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import createStyles from '@material-ui/styles/createStyles';
import { PartnerAdVariant } from '@skyslope/partnerships-sdk';
import format from 'date-fns/format';
import { MD_MEDIA_QUERY, SENDER_UI_URL } from '../common/constants';
import { ReactComponent as AlreadySignedLogo } from '../images/already-signed-animation.svg';
import type { IExecutedDocument } from '../store/types';
import { colors } from '../theme';
import { AdPlacementIds } from './DigiSignAdsProvider';
import { DSAd } from './DSAd';

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const styles = (theme: Theme) =>
  createStyles({
    dialogPaper: {
      overflow: 'initial',
    },
    dialogTitle: {
      fontSize: '26px',
      textAlign: 'center',
      color: colors.dusk,
      fontWeight: 'bold',
      padding: theme.spacing(3),
    },
    dialogAction: {
      justifyContent: 'center',
      paddingBottom: theme.spacing(3),
      flexFlow: 'column',
    },
    dialogContent: {
      paddingTop: theme.spacing(3),
      overflow: 'visible',
    },
    dsAdContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      margin: '24px',
    },
    dsAdMobileContainer: {
      position: 'absolute',
      bottom: '-5px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
    },
    buttonGroup: {
      borderRadius: '20px',
      marginBottom: theme.spacing(),
    },
    buttonPadding: {
      padding: `${theme.spacing(2)}px 0`,
    },
    popper: {
      zIndex: 100,
      width: '370px',
      margin: '99px 0 0 0',
      '@media not all and (min-resolution:.001dpcm)': {
        '@supports (-webkit-appearance:none)': {
          margin: '49px 0 0 0',
        },
      },
    },
    svgContainer: {
      padding: `0 ${theme.spacing(3)}px`,
    },
    noBtnPadding: {
      padding: 0,
    },
    text: {
      color: colors.dusk,
      textAlign: 'center',
    },
    mobileButtonText: {
      fontSize: '14px',
    },
    imDoneButton: {
      width: '326px',
      backgroundColor: '#fff',
      color: '#0059da',
      border: '2px solid #0059DA',
      borderRadius: '100px',
    },
  });

interface IProps extends WithStyles<typeof styles> {
  envelopeId: string;
  documents: Array<IExecutedDocument>;
  dateSigned: string;
  downloadEnvelopeFile: (downloadType: string) => void;
  redirectUrl: string | undefined;
}

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const AlreadySignedAlert = (props: IProps) => {
  const anchorRef: any = React.createRef();
  const [open, setOpen] = useState(true);
  const [listOpen, setListOpen] = useState(false);
  const isMultipleDocs = props.documents.length > 1;
  let isFullScreen = useMediaQuery(MD_MEDIA_QUERY);
  const { documents, dateSigned, classes, downloadEnvelopeFile, redirectUrl } = props;
  const buttonClass = isFullScreen ? classes.mobileButtonText : '';
  const urlParams = new URLSearchParams(window.location.search);
  const signNow = urlParams.get('signNow');
  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const handleClose = () => {
    setOpen(false);
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const handleToggleList = (_event: any) => {
    setListOpen(!listOpen);
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const handleCloseList = (event: any) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setListOpen(false);
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const handleSignNowComplete = () => {
    if (redirectUrl) {
      window.location.href = redirectUrl;
    } else {
      window.location.href = SENDER_UI_URL || '';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      disablePortal
      fullScreen={isFullScreen}
      scroll="body"
      classes={{ paper: classes.dialogPaper }}
    >
      <div className={isFullScreen ? classes.dsAdMobileContainer : classes.dsAdContainer}>
        <DSAd
          id={AdPlacementIds.SIGNED}
          adVariant={isFullScreen ? PartnerAdVariant.MobileBanner : PartnerAdVariant.Wide}
        />
      </div>
      <DialogTitle className={classes.dialogTitle} disableTypography={true} id="alert-dialog-title">
        Thanks for the Autograph
      </DialogTitle>
      <div className={classes.svgContainer}>
        <AlreadySignedLogo />
      </div>
      <DialogContent className={classes.dialogContent}>
        <DialogContentText id="alert-dialog-description" className={classes.text}>
          You signed {documents.length > 1 ? 'these documents' : 'this document'} on{' '}
          {format(dateSigned, 'MMMM Do, YYYY [at] h:mm a')}.
        </DialogContentText>
      </DialogContent>
      <DialogActions disableSpacing className={classes.dialogAction}>
        <ButtonGroup
          color="primary"
          variant="contained"
          aria-label="Split Button"
          ref={anchorRef}
          className={classes.buttonGroup}
        >
          <Button
            onClick={() => downloadEnvelopeFile('pdf')}
            className={buttonClass}
            onTouchEnd={() => downloadEnvelopeFile('pdf')}
          >
            Download Signed Documents
          </Button>
          {isMultipleDocs ? (
            <Button
              size="small"
              aria-owns={open ? 'menu-list-grow' : undefined}
              aria-haspopup="true"
              onClick={handleToggleList}
              onTouchEnd={handleToggleList}
              data-spec="dropdown"
            >
              <ArrowDropDownIcon />
            </Button>
          ) : null}
        </ButtonGroup>
        <Popper
          open={listOpen}
          anchorEl={anchorRef.current}
          className={classes.popper}
          transition
          disablePortal
          placement="bottom"
          style={{ position: 'fixed', top: 'unset', left: 'unset' }}
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin: placement === 'bottom' ? 'center bottom' : 'center top',
              }}
            >
              <Paper id="menu-list-grow">
                <ClickAwayListener onClickAway={handleCloseList}>
                  <MenuList>
                    <MenuItem
                      onClick={() => downloadEnvelopeFile('pdf')}
                      onTouchEnd={() => downloadEnvelopeFile('pdf')}
                    >
                      {isMultipleDocs ? 'Download Single PDF' : 'Download PDF'}
                    </MenuItem>
                    {isMultipleDocs ? (
                      <MenuItem
                        data-spec="isMultipleDocs"
                        onClick={() => downloadEnvelopeFile('zip')}
                        onTouchEnd={() => downloadEnvelopeFile('zip')}
                      >
                        Download Individual PDFs (Zip)
                      </MenuItem>
                    ) : null}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
        {signNow ? (
          <Button
            className={`${classes.buttonGroup} ${classes.imDoneButton}`}
            variant="contained"
            color="primary"
            onClick={handleSignNowComplete}
            data-spec="sign-now-complete"
          >
            I'm done
          </Button>
        ) : null}
        <Button onClick={handleClose} onTouchEnd={handleClose} className={classes.noBtnPadding} color="primary">
          View Signed Documents
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// eslint-disable-next-line import/no-default-export -- This code was written prior to enforcing lint rules
export default withStyles(styles)(AlreadySignedAlert);
